// Shared print/PDF template for Invoices and Sales Orders.
// Used by BOTH the Admin portal and the Clinic portal so the output is identical.
// Seller (clinic) GSTIN + address are shown exactly as provided — blank when missing
// (no hardcoded default). The document opens the browser's print / Save-as-PDF dialog.

const fmtCurrency = (n) => {
  const num = Number(n || 0);
  return '\u20B9' + num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const INVOICE_TERMS = [
  'Goods once sold will not be returned.',
  'Any device outside the warranty period, received for repair will be charged as per the device fault and handling charge of Rs. 500/- will be collected.',
  'Hearing Aids (except CROS devices) are exempted goods from GST.',
  "All hearing aid sold will be covered as per the company warranty's terms and conditions.",
  'Hearing aids may be exchanged for a higher model within 30 days.',
  'In case any cheque is dishonored by any reason, will be charged Rs 500/-.',
];

// Sub Total = Qty x Rate (before discount); GST split into CGST/SGST or IGST.
export function invoiceTotals(doc = {}) {
  const lines = doc.line_items || [];
  const num = (v) => Number(v || 0);
  const gross = lines.reduce((s, it) => s + num(it.quantity ?? 1) * num(it.rate ?? it.price), 0) || num(doc.subtotal);
  const discount = lines.reduce((s, it) => s + num(it.discount_amount ?? it.discount), 0) + num(doc.additional_discount);
  const tax = num(doc.total_tax ?? doc.gst_amount ?? (num(doc.cgst) + num(doc.sgst) + num(doc.igst)));
  let cgst = num(doc.cgst), sgst = num(doc.sgst), igst = num(doc.igst);
  if (tax > 0 && !(cgst || sgst || igst)) {
    if (doc.is_inter_state) igst = tax; else { cgst = Math.round(tax * 50) / 100; sgst = tax - cgst; }
  }
  return { gross, discount, taxable: Math.max(gross - discount, 0), tax, cgst, sgst, igst };
}

const esc = (s) => String(s == null ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Render an Invoice or Sales Order in the shared HearClear template.
 * @param {object} doc  invoice or sales-order object
 * @param {object} opts { type: 'invoice'|'so', customer, payments }
 */
export function printBusinessDocument(doc, opts = {}) {
  const type = opts.type === 'so' ? 'so' : 'invoice';
  const isSO = type === 'so';
  const customer = opts.customer || doc.customer || {};
  const payments = opts.payments || doc.payments || [];

  const w = window.open('', '_blank');
  if (!w) return;
  const logoUrl = window.location.origin + '/hearclear-logo.png';

  const docNumber = isSO ? (doc.so_number || doc.salesorder_number || '') : (doc.invoice_number || '');
  const docDate = isSO ? (doc.date || doc.order_date || doc.so_date || '') : (doc.invoice_date || doc.date || '');
  const dueOrShip = isSO ? (doc.expected_shipping_date || doc.expected_delivery_date || '') : (doc.due_date || docDate);

  const addrToStr = (a) => {
    if (!a) return '';
    if (typeof a === 'string') {
      const s = a.trim();
      if (s.startsWith('{') && s.endsWith('}')) {
        try { a = JSON.parse(s.replace(/'/g, '"')); } catch { return ''; }
      } else {
        return s;
      }
    }
    if (typeof a === 'object') {
      return [a.attention, a.address, a.street, a.street2, a.city, a.state, a.zip || a.zip_code || a.pincode, a.country]
        .map(v => (v == null ? '' : String(v).trim()))
        .filter(Boolean).join(', ');
    }
    return '';
  };

  const custName = customer.contact_name || customer.name || doc.customer_name || '';
  const custPhone = customer.phone || doc.customer_phone || '';
  const custAddress = addrToStr(customer.billing_address) || addrToStr(customer.address) || addrToStr(doc.customer_address) || '';
  const custGstin = customer.gstin || doc.customer_gstin || '';
  const placeOfSupply = customer.place_of_supply || doc.place_of_supply || (typeof customer.state === 'string' ? customer.state : '') || doc.customer_state || '';

  const clinicName = doc.clinic_name || doc.location_name || '';
  const clinicAddress = doc.clinic_address || '';
  const clinicGstin = doc.clinic_gstin || '';

  const lineItems = doc.line_items || [];
  const hasDiscount = lineItems.some(it => Number(it.discount ?? it.discount_amount ?? 0) > 0);

  const rowsHtml = lineItems.map((item, idx) => {
    const serials = (item.serial_numbers || (item.serial_number ? [item.serial_number] : []) || []).filter(Boolean).join(', ');
    const qty = item.quantity ?? 1;
    const rate = item.rate ?? item.price ?? 0;
    const disc = Number(item.discount ?? item.discount_amount ?? 0);
    const amount = item.amount ?? item.item_total ?? item.total ?? (qty * rate - disc);
    return `<tr>
      <td style="padding:8px 12px;border-bottom:1px solid #E2E8F0;text-align:center">${idx + 1}</td>
      <td style="padding:8px 12px;border-bottom:1px solid #E2E8F0">${esc(item.name || item.product || item.description || 'Item')}${serials ? '<br><span style="font-size:10px;color:#666">S/N: ' + esc(serials) + '</span>' : ''}</td>
      <td style="padding:8px 12px;border-bottom:1px solid #E2E8F0;text-align:center;font-size:12px">${esc(item.hsn_sac || '-')}</td>
      <td style="padding:8px 12px;border-bottom:1px solid #E2E8F0;text-align:center">${qty}</td>
      <td style="padding:8px 12px;border-bottom:1px solid #E2E8F0;text-align:right">${fmtCurrency(rate)}</td>
      ${hasDiscount ? `<td style="padding:8px 12px;border-bottom:1px solid #E2E8F0;text-align:right;color:#DC2626">${disc > 0 ? '-' + fmtCurrency(disc) : '-'}</td>` : ''}
      <td style="padding:8px 12px;border-bottom:1px solid #E2E8F0;text-align:right;font-weight:600">${fmtCurrency(amount)}</td>
    </tr>`;
  }).join('');

  const t = invoiceTotals(doc);
  const totalDiscount = t.discount;
  const subtotal = t.gross;
  const totalTax = t.tax;
  // Totals: trust the stored grand_total, but if it is missing or zero (data glitch) recompute it. (#9)
  const computedGrand = Math.round((subtotal - totalDiscount + totalTax) * 100) / 100;
  let grandTotal = doc.grand_total ?? doc.total;
  if (grandTotal == null || (Number(grandTotal) === 0 && computedGrand > 0)) grandTotal = computedGrand;
  grandTotal = Number(grandTotal) || 0;
  const paymentsMade = (payments || []).reduce((sum, p) => sum + Number(p.amount || 0), 0);
  // Balance: when payments are shown, derive it from grand − paid (authoritative) so a
  // stale stored balance never contradicts the payments listed on the document. (#11)
  const storedBal = doc.balance_due ?? doc.balance;
  const balanceDue = (payments && payments.length)
    ? Math.max(Math.round((grandTotal - paymentsMade) * 100) / 100, 0)
    : (storedBal != null ? Number(storedBal) : Math.max(grandTotal - paymentsMade, 0));

  const title = isSO ? 'SALES ORDER' : 'TAX INVOICE';

  const balanceBox = isSO ? '' : `
        <div class="balance-box">
          <div class="balance-label">Balance Due</div>
          <div class="balance-amount">${fmtCurrency(balanceDue)}</div>
        </div>`;

  const paymentRows = isSO ? '' : `
        ${paymentsMade > 0 ? '<tr class="payment-made"><td>Payment Made</td><td>(-) ' + fmtCurrency(paymentsMade) + '</td></tr>' : ''}
        <tr style="border-top:1px solid #ddd"><td style="font-weight:700">Balance Due</td><td style="font-weight:700;color:#0F4C81">${fmtCurrency(balanceDue)}</td></tr>`;

  const rightMetaLabel = isSO ? 'Expected Shipping' : 'Due Date';

  w.document.write(`<!DOCTYPE html><html><head><title>${esc(docNumber)}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; color: #333; padding: 30px; max-width: 800px; margin: 0 auto; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; }
    .logo-section img { height: 60px; }
    .company-name { font-size: 14px; font-weight: 700; margin-top: 6px; color: #333; }
    .company-details { font-size: 11px; color: #555; line-height: 1.5; margin-top: 4px; }
    .inv-title-section { text-align: right; }
    .inv-title { font-size: 20px; font-weight: 700; color: #0F4C81; margin-bottom: 4px; }
    .inv-number { font-size: 14px; color: #333; margin-bottom: 8px; }
    .balance-box { background: #EBF3FA; border: 1px solid #BFDBFE; border-radius: 6px; padding: 8px 16px; text-align: right; }
    .balance-label { font-size: 10px; color: #666; text-transform: uppercase; }
    .balance-amount { font-size: 18px; font-weight: 700; color: #0F4C81; }
    .divider { border-top: 2px solid #0F4C81; margin: 16px 0; }
    .info-row { display: flex; justify-content: space-between; margin-bottom: 20px; }
    .info-block { flex: 1; }
    .info-block h4 { font-size: 11px; color: #0F4C81; text-transform: uppercase; font-weight: 700; margin-bottom: 6px; border-bottom: 1px solid #ddd; padding-bottom: 3px; }
    .info-block p { font-size: 12px; line-height: 1.6; }
    .info-block .label { color: #888; font-size: 10px; }
    table { width: 100%; border-collapse: collapse; margin: 16px 0; }
    thead th { background: #0F4C81; color: white; font-size: 11px; padding: 10px 12px; text-align: left; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
    thead th:first-child { border-radius: 4px 0 0 0; }
    thead th:last-child { border-radius: 0 4px 0 0; }
    .totals { margin-top: 12px; display: flex; justify-content: flex-end; }
    .totals-table { width: 280px; }
    .totals-table tr td { padding: 6px 12px; font-size: 12px; }
    .totals-table tr td:last-child { text-align: right; font-weight: 600; }
    .totals-table .grand-total td { font-size: 14px; font-weight: 700; border-top: 2px solid #0F4C81; padding-top: 10px; color: #0F4C81; }
    .totals-table .payment-made td { color: #DC2626; }
    .terms { margin-top: 30px; border-top: 1px solid #ddd; padding-top: 12px; }
    .terms h4 { font-size: 11px; color: #0F4C81; font-weight: 700; margin-bottom: 8px; }
    .terms ol { font-size: 10px; color: #555; padding-left: 18px; line-height: 1.8; }
    @media print { body { padding: 15px; } }
  </style></head><body>

  <div class="header">
    <div class="logo-section">
      <img src="${logoUrl}" alt="HearClear" onerror="this.style.display='none'" />
      <div class="company-name">GANG OH INNOVATIONS PVT LTD</div>
      <div class="company-details">
        ${clinicName ? esc(clinicName) + '<br>' : ''}
        ${clinicAddress ? esc(clinicAddress) + '<br>' : ''}
        ${clinicGstin ? 'GSTIN: ' + esc(clinicGstin) : ''}
      </div>
    </div>
    <div class="inv-title-section">
      <div class="inv-title">${title}</div>
      <div class="inv-number">${esc(docNumber)}</div>
      ${balanceBox}
    </div>
  </div>

  <div class="divider"></div>

  <div class="info-row">
    <div class="info-block">
      <h4>Bill To</h4>
      <p><strong>${esc(custName)}</strong></p>
      ${custAddress ? '<p>' + esc(custAddress) + '</p>' : ''}
      ${custPhone ? '<p>Mobile: ' + esc(custPhone) + '</p>' : ''}
      ${custGstin ? '<p><strong>GSTIN: ' + esc(custGstin) + '</strong></p>' : ''}
      <p>Place of Supply: <strong>${esc(placeOfSupply || '-')}</strong></p>
    </div>
    <div class="info-block" style="text-align:right">
      <h4>${isSO ? 'Order Details' : 'Invoice Details'}</h4>
      <p><span class="label">${isSO ? 'Order Date' : 'Invoice Date'}:</span> ${fmtDate(docDate)}</p>
      ${isSO ? '' : '<p><span class="label">Terms:</span> Due on Receipt</p>'}
      ${dueOrShip ? `<p><span class="label">${rightMetaLabel}:</span> ${fmtDate(dueOrShip)}</p>` : ''}
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th style="width:40px;text-align:center">#</th>
        <th>Item &amp; Description</th>
        <th style="width:80px;text-align:center">HSN/SAC</th>
        <th style="width:50px;text-align:center">Qty</th>
        <th style="width:100px;text-align:right">Rate</th>
        ${hasDiscount ? '<th style="width:90px;text-align:right">Discount</th>' : ''}
        <th style="width:110px;text-align:right">Amount</th>
      </tr>
    </thead>
    <tbody>${rowsHtml}</tbody>
  </table>

  <div class="totals">
    <table class="totals-table">
      <tr><td>Sub Total</td><td>${fmtCurrency(subtotal)}</td></tr>
      ${totalDiscount > 0 ? '<tr><td>Total Discount</td><td style="color:#DC2626">(-) ' + fmtCurrency(totalDiscount) + '</td></tr>' : ''}
      ${t.cgst ? '<tr><td>CGST</td><td>' + fmtCurrency(t.cgst) + '</td></tr>' : ''}
      ${t.sgst ? '<tr><td>SGST</td><td>' + fmtCurrency(t.sgst) + '</td></tr>' : ''}
      ${t.igst ? '<tr><td>IGST</td><td>' + fmtCurrency(t.igst) + '</td></tr>' : ''}
      <tr class="grand-total"><td>Total</td><td>${fmtCurrency(grandTotal)}</td></tr>
      ${paymentRows}
    </table>
  </div>

  <div class="terms">
    <h4>Terms &amp; Conditions</h4>
    <ol>
      ${INVOICE_TERMS.map(t => `<li>${esc(t)}</li>`).join('')}
    </ol>
  </div>

  <script>window.onload=()=>window.print()</script></body></html>`);
  w.document.close();
}

export const printInvoiceDocument = (invoice, opts = {}) => printBusinessDocument(invoice, { ...opts, type: 'invoice' });
export const printSalesOrderDocument = (so, opts = {}) => printBusinessDocument(so, { ...opts, type: 'so' });
